// WMS (Warehouse Management System) Types

// ============================================================================
// Warehouse Location Types
// ============================================================================

export interface Warehouse {
  id: string;
  code: string;
  name: string;
  address?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface WarehouseFormData {
  code: string;
  name: string;
  address?: string;
  is_active?: boolean;
}

export interface Hallway {
  id: string;
  warehouse_id: string;
  code: string;
  full_code: string;
  name?: string;
  sequence: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // Joined data
  warehouse?: Warehouse;
}

export interface HallwayFormData {
  warehouse_id: string;
  code: string;
  name?: string;
  sequence?: number;
  is_active?: boolean;
}

export interface Rack {
  id: string;
  hallway_id: string;
  code: string;
  full_code: string;
  barcode?: string;
  rack_type: RackType;
  max_weight_kg?: number;
  sequence: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // Joined data
  hallway?: Hallway;
}

export type RackType =
  | "storage"
  | "picking"
  | "receiving"
  | "shipping"
  | "qc"
  | "production";

export const RACK_TYPE_LABELS: Record<RackType, string> = {
  storage: "Almacenamiento",
  picking: "Picking",
  receiving: "Recepción",
  shipping: "Despacho",
  qc: "Control de Calidad",
  production: "Producción",
};

export interface RackFormData {
  hallway_id: string;
  code: string;
  barcode?: string;
  rack_type?: RackType;
  max_weight_kg?: number;
  sequence?: number;
  is_active?: boolean;
}

export interface Position {
  id: string;
  rack_id: string;
  code: string;
  full_code: string;
  barcode?: string;
  level: number;
  max_weight_kg?: number;
  max_volume_m3?: number;
  distance_from_shipping: number;
  ergonomic_score: number;
  slotting_score: number;
  preferred_velocity_class?: "A" | "B" | "C";
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // Joined data
  rack?: Rack;
}

export interface PositionFormData {
  rack_id: string;
  code: string;
  barcode?: string;
  level?: number;
  max_weight_kg?: number;
  max_volume_m3?: number;
  distance_from_shipping?: number;
  ergonomic_score?: number;
  preferred_velocity_class?: "A" | "B" | "C";
  is_active?: boolean;
}

// ============================================================================
// Pallet Types
// ============================================================================

export type PalletStatus =
  | "created"
  | "in_production"
  | "ready_for_warehouse"
  | "ready_for_ingestion"
  | "in_transit"
  | "stored"
  | "in_warehouse"
  | "partial"
  | "partially_picked"
  | "picked_full"
  | "depleted"
  | "empty"
  | "damaged";

export const PALLET_STATUS_LABELS: Record<PalletStatus, string> = {
  created: "Creado",
  in_production: "En Producción",
  ready_for_warehouse: "Listo para Bodega",
  ready_for_ingestion: "Listo para Ingreso",
  in_transit: "En Tránsito",
  stored: "Almacenado",
  in_warehouse: "En Bodega",
  partial: "Parcial",
  partially_picked: "Parcialmente Pickeado",
  picked_full: "Retirado Completo",
  depleted: "Agotado",
  empty: "Vacío",
  damaged: "Dañado",
};

export interface PalletConfiguration {
  id: string;
  product_id: string;
  boxes_per_pallet: number;
  bottles_per_box: number;
  pallet_weight_kg?: number;
  pallet_height_m?: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface PalletConfigurationFormData {
  product_id: string;
  boxes_per_pallet: number;
  bottles_per_box: number;
  pallet_weight_kg?: number;
  pallet_height_m?: number;
  is_active?: boolean;
}

export type PalletType = "single" | "mixed";

export const PALLET_TYPE_LABELS: Record<PalletType, string> = {
  single: "Simple",
  mixed: "Mixto",
};

export interface PalletBatch {
  id: string;
  pallet_id: string;
  production_batch_id: string;
  product_id: string;
  box_count: number;
  lot_number?: string;
  expiration_date?: string;
  added_at: string;
  added_by?: string;
  notes?: string;
  // Joined data
  product?: { id: string; short_name: string };
  production_batch?: {
    id: string;
    batch_number: string;
    expiry_date?: string;
  };
}

export interface PalletBatchFormData {
  pallet_id: string;
  production_batch_id: string;
  product_id: string;
  box_count: number;
  lot_number?: string;
  expiration_date?: string;
  notes?: string;
}

export interface Pallet {
  id: string;
  code: string;
  barcode?: string;
  production_batch_id?: string;
  product_id?: string;
  pallet_type: PalletType;
  initial_box_count: number;
  current_box_count: number;
  boxes_per_pallet: number;
  lot_number?: string;
  expiration_date?: string;
  earliest_expiration?: string;
  weight_kg?: number;
  status: PalletStatus;
  position_id?: string;
  // Production tracking
  sequence_number?: number;
  production_started_at?: string;
  production_ended_at?: string;
  produced_by?: string;
  sticker_printed_at?: string;
  sticker_printed_by?: string;
  // SCAN 1: Exit Production (legacy)
  exited_production_at?: string;
  exited_production_by?: string;
  // SCAN 2: Enter Warehouse
  received_at?: string;
  received_by?: string;
  // SCAN 3: Exit Warehouse
  last_picked_at?: string;
  last_picked_by?: string;
  shipped_at?: string;
  created_at: string;
  updated_at: string;
  // Joined data
  product?: { id: string; short_name: string };
  position?: Position;
  batches?: PalletBatch[];
  produced_by_profile?: { name: string; lastname: string };
}

// ============================================================================
// Pallet Relocation Types
// ============================================================================

export type RelocationStep =
  | "scan_pallet"
  | "view_details"
  | "scan_destination"
  | "confirm"
  | "success";

export interface PalletRelocationState {
  step: RelocationStep;
  pallet?: Pallet;
  sourcePosition?: Position;
  destinationPosition?: Position;
  error?: string;
}

export interface RelocationResult {
  success: boolean;
  pallet_id: string;
  from_position_id?: string;
  to_position_id: string;
  movement_id?: string;
  error?: string;
}

export interface PalletFilters {
  status?: PalletStatus;
  pallet_type?: PalletType;
  position_id?: string;
  warehouse_id?: string;
  product_id?: string;
  has_stock?: boolean;
  search?: string;
}

// ============================================================================
// Ingestion Request Types
// ============================================================================

export type IngestionRequestStatus =
  | "pending"
  | "assigned"
  | "scanning"
  | "putaway"
  | "completed"
  | "cancelled";

export const INGESTION_STATUS_LABELS: Record<IngestionRequestStatus, string> = {
  pending: "Pendiente",
  assigned: "Asignado",
  scanning: "Escaneando",
  putaway: "Ubicando",
  completed: "Completado",
  cancelled: "Cancelado",
};

// Workflow step for the simplified 3-step inbound workflow
export type IngestionWorkflowStep =
  | "pending" // Waiting for worker to claim
  | "claimed" // Worker claimed, ready to scan batch
  | "verified" // Batch barcode verified, ready to scan position
  | "completed" // Batch placed in position
  | "cancelled"; // Cancelled

export const WORKFLOW_STEP_LABELS: Record<IngestionWorkflowStep, string> = {
  pending: "Pendiente",
  claimed: "Reclamado",
  verified: "Verificado",
  completed: "Completado",
  cancelled: "Cancelado",
};

export interface IngestionRequest {
  id: string;
  production_batch_id: string;
  product_id: string;
  expected_pallets: number;
  received_pallets: number;
  expected_boxes: number;
  received_boxes: number;
  status: IngestionRequestStatus;
  priority: number;
  assigned_to?: string;
  assigned_at?: string;
  started_at?: string;
  completed_at?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  // Workflow tracking (3-step inbound)
  workflow_step?: IngestionWorkflowStep;
  verified_at?: string;
  placed_position_id?: string;
  placed_quantity?: number;
  // Joined data
  production_batch?: {
    id: string;
    batch_number: string;
    expiry_date: string;
  };
  product?: {
    id: string;
    short_name: string;
  };
  assigned_user?: {
    id: string;
    name: string;
  };
  assigned_profile?: {
    name: string;
    lastname: string;
  };
  placed_position?: Position;
}

export type IngestionPalletStatus =
  | "pending"
  | "scanned"
  | "placed"
  | "discrepancy";

export type DiscrepancyType = "box_count_mismatch" | "wrong_pallet" | "damaged";

export interface IngestionPallet {
  id: string;
  ingestion_request_id: string;
  pallet_id?: string;
  scanned_barcode?: string;
  expected_boxes: number;
  received_boxes?: number;
  destination_position_id?: string;
  status: IngestionPalletStatus;
  discrepancy_type?: DiscrepancyType;
  discrepancy_notes?: string;
  scanned_by?: string;
  scanned_at?: string;
  placed_at?: string;
  created_at: string;
  // Joined data
  pallet?: Pallet;
  destination_position?: Position;
}

// ============================================================================
// Inventory Types
// ============================================================================

export interface WmsInventory {
  id: string;
  position_id: string;
  pallet_id?: string;
  product_id: string;
  production_batch_id?: string;
  lot_number?: string;
  box_count: number;
  reserved_boxes: number;
  expiration_date?: string;
  received_at: string;
  updated_at: string;
  // Computed
  available_boxes?: number;
  // Joined data
  position?: Position;
  pallet?: Pallet;
  product?: { id: string; short_name: string };
}

export interface InventoryAllocation {
  inventory_id: string;
  position_id: string;
  position_code: string;
  pallet_id?: string;
  lot_number?: string;
  quantity: number;
  expiration_date?: string;
  days_until_expiry?: number;
}

// ============================================================================
// Movement Types
// ============================================================================

export type MovementType = "receipt" | "pick" | "transfer" | "adjustment";

export const MOVEMENT_TYPE_LABELS: Record<MovementType, string> = {
  receipt: "Recepción",
  pick: "Picking",
  transfer: "Transferencia",
  adjustment: "Ajuste",
};

export type MovementReferenceType =
  | "sales_request"
  | "production_batch"
  | "ingestion_request"
  | "manual";

export interface WmsMovement {
  id: string;
  movement_type: MovementType;
  pallet_id?: string;
  product_id: string;
  lot_number?: string;
  box_quantity: number;
  from_position_id?: string;
  to_position_id?: string;
  reference_type?: MovementReferenceType;
  reference_id?: string;
  scanned_pallet_barcode?: string;
  scanned_location_barcode?: string;
  reason?: string;
  notes?: string;
  performed_by?: string;
  performed_at: string;
  created_at: string;
  // Joined data
  product?: { id: string; short_name: string };
  from_position?: Position;
  to_position?: Position;
  performer?: { id: string; name: string };
}

// ============================================================================
// Pick Task Types
// ============================================================================

export type PickTaskStatus =
  | "pending"
  | "assigned"
  | "in_progress"
  | "packed"
  | "completed"
  | "cancelled";

export const PICK_TASK_STATUS_LABELS: Record<PickTaskStatus, string> = {
  pending: "Pendiente",
  assigned: "Asignado",
  in_progress: "En Progreso",
  packed: "Empacado",
  completed: "Completado",
  cancelled: "Cancelado",
};

export interface PickTask {
  id: string;
  sales_request_id: string;
  warehouse_id?: string;
  status: PickTaskStatus;
  priority: number;
  assigned_to?: string;
  assigned_at?: string;
  started_at?: string;
  completed_at?: string;
  created_at: string;
  updated_at: string;
  // Joined data
  sales_request?: {
    id: string;
    client?: { name: string };
    delivery_date?: string;
  };
  warehouse?: Warehouse;
  assigned_user?: { id: string; name: string };
  items?: PickTaskItem[];
}

export type PickTaskItemStatus = "pending" | "picked" | "short" | "skipped";

export interface PickTaskItem {
  id: string;
  pick_task_id: string;
  product_id: string;
  inventory_id?: string;
  requested_quantity: number;
  picked_quantity: number;
  source_position_id?: string;
  lot_number?: string;
  pick_sequence: number;
  status: PickTaskItemStatus;
  scanned_location: boolean;
  scanned_product: boolean;
  picked_at?: string;
  picked_by?: string;
  created_at: string;
  // Joined data
  product?: { id: string; short_name: string };
  source_position?: Position;
}

// ============================================================================
// Dashboard / Statistics Types
// ============================================================================

export interface WmsDashboardStats {
  pending_ingestion_requests: number;
  in_progress_picks: number;
  ready_to_ship: number;
  total_positions: number;
  occupied_positions: number;
  total_boxes_in_stock: number;
}

export interface InventorySummary {
  product_id: string;
  product_name: string;
  total_boxes: number;
  reserved_boxes: number;
  available_boxes: number;
  pallet_count: number;
  earliest_expiry?: string;
}

// ============================================================================
// Filter Types
// ============================================================================

export interface IngestionRequestFilters {
  status?: IngestionRequestStatus;
  product_id?: string;
  assigned_to?: string;
}

export interface PickTaskFilters {
  status?: PickTaskStatus;
  warehouse_id?: string;
  assigned_to?: string;
  priority?: number;
}

export interface InventoryFilters {
  product_id?: string;
  warehouse_id?: string;
  has_stock?: boolean;
}

// ============================================================================
// Flat Location Types (for All Locations Page)
// ============================================================================

export type LocationType = "hallway" | "rack" | "position";

export const LOCATION_TYPE_LABELS: Record<LocationType, string> = {
  hallway: "Pasillo",
  rack: "Rack",
  position: "Posición",
};

export interface FlatLocation {
  id: string;
  type: LocationType;
  code: string;
  full_code: string;
  warehouse_id: string;
  warehouse_code: string;
  warehouse_name: string;
  hallway_id?: string;
  hallway_code?: string;
  rack_id?: string;
  rack_code?: string;
  level?: number;
  is_active: boolean;
  // Additional fields for display
  rack_type?: RackType;
  barcode?: string;
}

export interface LocationFilters {
  type?: LocationType;
  warehouse_id?: string;
  hallway_id?: string;
  rack_id?: string;
  is_active?: boolean;
}

// ============================================================================
// Warehouse Grid Types (for full warehouse visualization)
// ============================================================================

export interface PositionInventoryData {
  position: Position;
  inventory: WmsInventory[];
  totalBoxes: number;
  reservedBoxes: number;
  availableBoxes: number;
  hasStock: boolean;
}

export interface RackInventoryData {
  rack: Rack;
  positions: PositionInventoryData[];
  totalPositions: number;
  occupiedPositions: number;
  occupancyPercent: number;
}

export interface HallwayInventoryData {
  hallway: Hallway;
  racks: RackInventoryData[];
  totalPositions: number;
  occupiedPositions: number;
  occupancyPercent: number;
}

export interface WarehouseInventoryData {
  warehouse: Warehouse;
  hallways: HallwayInventoryData[];
  totalPositions: number;
  occupiedPositions: number;
  occupancyPercent: number;
}

// ============================================================================
// Scanning Types
// ============================================================================

export type ScanEventType = "batch_scan" | "location_scan" | "product_scan";
export type ScanActionResult = "success" | "error" | "validation_failed";
export type ScanWorkflowType = "wms_receiving" | "wms_picking" | "wms_transfer";
export type ScanBarcodeType = "BATCH" | "LOC" | "PROD" | "UNKNOWN";

export interface ScanEvent {
  id: string;
  event_type: ScanEventType;
  raw_barcode: string;
  parsed_type?: ScanBarcodeType;
  parsed_identifier?: string;
  station_code?: string;
  action_taken?: string;
  action_result?: ScanActionResult;
  error_message?: string;
  related_entity_type?: string;
  related_entity_id?: string;
  workflow_context?: ScanWorkflowContext;
  scanned_by?: string;
  scanned_at: string;
  device_info?: ScanDeviceInfo;
}

export interface ScanWorkflowContext {
  workflow_type: ScanWorkflowType;
  related_entity_id?: string;
  expected_scan_type?: ScanBarcodeType;
  step?: string;
  additional_data?: Record<string, unknown>;
}

export interface ScanDeviceInfo {
  user_agent?: string;
  platform?: string;
  is_mobile?: boolean;
}

export interface ProcessScanParams {
  raw_barcode: string;
  station_code?: string;
  workflow_context: ScanWorkflowContext;
  additional_data?: {
    quantity?: number;
    notes?: string;
    destination_position_id?: string;
  };
  scanned_by: string;
}

export interface ProcessScanResult {
  success: boolean;
  scan_event_id?: string;
  parsed: {
    type: ScanBarcodeType;
    identifier: string;
    is_valid: boolean;
    components?: Record<string, string>;
  };
  action: {
    type: string;
    result: ScanActionResult;
    message: string;
  };
  entity?: {
    type: string;
    id: string;
    data: Record<string, unknown>;
  };
  next_step?: {
    expected_scan_type: ScanBarcodeType;
    instructions: string;
  };
  error?: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  };
}

// ============================================================================
// Picking Orders Types (for pre-invoice picking workflow)
// ============================================================================

export interface PickingOrderItem {
  product_id: string;
  product_name: string;
  quantity: number;
  stock_available: number;
  sufficient: boolean;
}

export interface PickingOrder {
  id: string;
  created_at: string;
  delivery_date?: string;
  total_amount: number;
  items_count: number;
  stock_available: boolean;
  items: PickingOrderItem[];
  // Pick task info if already started
  pick_task_id?: string;
  pick_task_status?: PickTaskStatus;
}

export interface PickingOrdersByClient {
  client_id: string;
  client_name: string;
  delivery_address: {
    street?: string;
    number?: string;
    city?: string;
    comuna?: string;
    full_address?: string;
  };
  orders: PickingOrder[];
}

export interface StockAvailabilityResult {
  available: boolean;
  items: PickingOrderItem[];
}

// ============================================================================
// Production Pallet Types
// ============================================================================

export interface BatchPallet {
  id: string;
  code: string;
  barcode: string;
  sequence_number: number;
  production_started_at: string | null;
  production_ended_at: string | null;
  duration_minutes: number | null;
  produced_by: string | null;
  producer_name: string;
  initial_box_count: number;
  current_box_count: number;
  status: PalletStatus;
  sticker_printed_at: string | null;
  position_id: string | null;
  position_code: string | null;
  received_at: string | null;
}

export interface PalletValidationResult {
  is_valid: boolean;
  pallet_id: string | null;
  pallet_code: string | null;
  product_id: string | null;
  product_name: string | null;
  box_count: number | null;
  lot_number: string | null;
  batch_number: string | null;
  expiration_date: string | null;
  sequence_number: number | null;
  total_pallets_in_batch: number | null;
  production_ended_at: string | null;
  current_status: string | null;
  error_message: string | null;
}

// ============================================================================
// Stock Consultation Types
// ============================================================================

/**
 * Stock summary for a product, including available, reserved, and blocked quantities
 */
export interface StockSummary {
  product_id: string;
  sku: string | null;
  product_name: string;
  total_physical_stock: number;
  wms_total_boxes: number;
  wms_available_boxes: number;
  wms_reserved_boxes: number;
  blocked_by_expiration: number;
  active_reservations: number;
  transicion_boxes: number;
}

/**
 * Inventory detail row for a product by position
 */
export interface ProductInventoryDetail {
  inventory_id: string;
  position_id: string;
  position_code: string;
  warehouse_name: string;
  pallet_id: string | null;
  pallet_barcode: string | null;
  lot_number: string | null;
  box_count: number;
  reserved_boxes: number;
  available_boxes: number;
  expiration_date: string | null;
  days_until_expiry: number | null;
  is_blocked: boolean;
  received_at: string;
}

/**
 * Lot traceability row showing where a lot exists in the warehouse
 */
export interface LotTraceabilityRow {
  inventory_id: string;
  position_code: string;
  warehouse_name: string;
  pallet_id: string | null;
  pallet_barcode: string | null;
  product_id: string;
  product_name: string;
  sku: string | null;
  box_count: number;
  reserved_boxes: number;
  available_boxes: number;
  expiration_date: string | null;
  days_until_expiry: number | null;
  received_at: string;
}

/**
 * Pallet movement history row
 */
export interface PalletMovementHistoryRow {
  movement_id: string;
  movement_type: string;
  box_quantity: number;
  lot_number: string | null;
  from_position: string | null;
  to_position: string | null;
  performed_by_email: string | null;
  performed_by_name: string | null;
  performed_at: string;
  reason: string | null;
  notes: string | null;
}

/**
 * Pallet search result with full details
 */
export interface PalletSearchResult {
  pallet_id: string;
  pallet_code: string;
  barcode: string | null;
  status: string;
  pallet_type: string | null;
  position_id: string | null;
  position_code: string | null;
  warehouse_name: string | null;
  product_id: string | null;
  product_name: string | null;
  sku: string | null;
  current_box_count: number;
  initial_box_count: number;
  boxes_per_pallet: number;
  lot_number: string | null;
  expiration_date: string | null;
  days_until_expiry: number | null;
  received_at: string | null;
  received_by_email: string | null;
  received_by_name: string | null;
}

/**
 * Position status with content details
 */
export interface PositionStatusResult {
  position_id: string;
  position_code: string;
  barcode: string | null;
  warehouse_name: string;
  hallway_code: string;
  rack_code: string;
  level_num: number;
  is_active: boolean;
  status: "empty" | "occupied" | "reserved";
  pallet_id: string | null;
  pallet_barcode: string | null;
  pallet_status: string | null;
  product_id: string | null;
  product_name: string | null;
  sku: string | null;
  box_count: number;
  reserved_boxes: number;
  lot_number: string | null;
  expiration_date: string | null;
  days_until_expiry: number | null;
}

/**
 * Product search result for SKU autocomplete
 */
export interface ProductSearchResult {
  product_id: string;
  sku: string | null;
  product_name: string;
  barcode: string | null;
  stock: number;
}

/**
 * Stock consultation query type
 */
export type StockQueryType = "sku" | "lpn" | "lot" | "position";

export const STOCK_QUERY_TYPE_LABELS: Record<StockQueryType, string> = {
  sku: "Por SKU",
  lpn: "Por LPN",
  lot: "Por Lote",
  position: "Por Posición",
};
